import React, { useEffect, useState } from 'react';
import { PrivateMeetingSessionDetailsListResponseType } from '../../../../lib/api';
import { PrivateMeetingSessionDetailsResponseType } from '../../../../lib/api';
import useSetKeyIdClass from '../../../../hooks/use-set-key-id-class';
import { IGroupedMeetingsList } from '../GridLayout/index';
import { getActiveTracks, getExpandButtonText } from '../helpers';
import { useCarousel } from './useCarousel';
import { CardComponent } from './Card';
import { EventType, IFilteredMeetings } from '../LayoutWrapper';
import './CarouselWrapper.scss';
import './Card.scss';
import DefaultCarousel from './DefaultCarousel';
import CarouselWrapperItem from './CarouselWrapperItem';
import { ITEMS_IN_SINGLE_ROW } from '../../../../lib/constants';
import Button from '../../../../components/button';
import CarouselByGrid from './CarouselByGrid';
import { DateFnsFormats, dateToFormattedString } from '../../../../lib/helpers/dateHelper';
import { MediaQueryContextType, useMediaQuery } from '../../../../lib/context-providers/media-query-provider';
import { DefaultCarouselView } from '../../../../lib/api/custom-page.types';

export type PrivateMeetingSessionList = PrivateMeetingSessionDetailsListResponseType;

interface ICarouselWrapper {
  event: EventType;
  selectedDate: string;
  showAllDates?: boolean;
  showPastMeetings: boolean;
  isCarouselViewByDay?: boolean;
  meetings: PrivateMeetingSessionDetailsResponseType[];
  searchedValue: string;
  setFilteredMeetingsCount: (data: IFilteredMeetings) => void;
  eventToken: string;
  transformedTimeZone: string;
  onSubscriptionChange?: (meetingId: string, subscribed: boolean) => void;
}

export interface ICarouselSection {
  event: EventType;
  selectedDate: string;
  showAllDates: boolean;
  showPastMeetings: boolean;
  meetings: IGroupedMeetingsList;
  activeMeetings: PrivateMeetingSessionList;
}

export interface ICarouselBox {
  event: EventType;
  selectedDate?: string;
  groupByDay?: boolean;
  meetings: PrivateMeetingSessionDetailsResponseType[];
  eventToken: string;
  transformedTimeZone: string;
  shouldRenderSeparateLine: boolean;
  onSubscriptionChange?: (meetingId: string, subscribed: boolean) => void;
}

export const Styles = {
  blockRow: 'w-full flex flex-row justify-between session-carousel-wrapper',
  blockCol: 'flex items-center flex-col',
  headerCssClass: 'font-bold text-left',
};

export const className = 'carouse-layout-session';
export const classNameSessionHeaderRight = `${className}--header-right`;
export const classNameSessionTableHeaderLeft = `${className}--table-header-left`;

export const renderSeparateLine = (con: boolean): JSX.Element | null => { return con ? <hr className='m-top-15px m-bottom-35px' /> : null; };

export const count = 1;
export const inc = 1;

export const CarouselBox = ({
  meetings,
  event,
  eventToken,
  groupByDay = false,
  transformedTimeZone,
  shouldRenderSeparateLine,
  onSubscriptionChange
}: ICarouselBox): JSX.Element | null => {
  const classNameEventDetails = 'editable-' + useSetKeyIdClass() + '-event-details';
  const classNameEventDetailsDate = `${classNameEventDetails}--date`;
  const { isMobile } = useMediaQuery() as MediaQueryContextType;

  const tracks = getActiveTracks(meetings);

  if (groupByDay) {
    return (
      <div className='w-full'>
        {!!meetings.length &&
          <DefaultCarousel
            meetings={meetings}
            event={event}
            eventToken={eventToken}
            transformedTimeZone={transformedTimeZone}
            shouldRenderSeparateLine={shouldRenderSeparateLine}
            onSubscriptionChange={onSubscriptionChange}
          />
        }
      </div>
    );
  }

  return (
    <div className='w-full'>
      {
        (tracks || []).map((track: string) => {
          const featured = meetings.filter(m => m.track === track && m.featured);
          const noFeatured = meetings.filter(m => m.track === track && !m.featured);
          const isTrack = Boolean(track);
          const sessions = featured.length + noFeatured.length;
          return (
            <CarouselWrapperItem
              key={track}
              featured={featured}
              noFeatured={noFeatured}
              isTrack={isTrack}
              sessions={sessions}
              event={event}
              eventToken={eventToken}
              transformedTimeZone={transformedTimeZone}
              track={track}
              classNameEventDetailsDate={classNameEventDetailsDate}
              shouldRenderSeparateLine={shouldRenderSeparateLine}
              onSubscriptionChange={onSubscriptionChange}
              isMobile={isMobile}
            />
          );
        })
      }
    </div>
  );
};

const CarouselWrapperByDay = (props: ICarouselWrapper): JSX.Element => {
  const {
    event,
    meetings,
    selectedDate,
    isCarouselViewByDay,
    showPastMeetings,
    searchedValue,
    setFilteredMeetingsCount,
    eventToken,
    transformedTimeZone,
    onSubscriptionChange,
  } = props;

  const {
    featuredMeetings,
    noFeaturedMeetings
  } = useCarousel({ meetings, selectedDate, showPastMeetings, searchedValue, groupByDay: true });
  const [isCarouselView, setIsCarouselView] = useState(() => event.defaultCarouselView === DefaultCarouselView.Collapsed);
  const sessions = featuredMeetings.length + noFeaturedMeetings.length;
  const isSessionsExist = Boolean(sessions);
  const classNameEventDetails = 'editable-' + useSetKeyIdClass() + '-event-details';
  const classNameEventDetailsDate = `${classNameEventDetails}--date`;
  const shortDate = dateToFormattedString(meetings[0].startDateTime, DateFnsFormats.ShortDayMonthDate, event.timeZone);
  const expandButtonText = getExpandButtonText(isCarouselView);
  const shouldRenderExpandButton = noFeaturedMeetings.length > ITEMS_IN_SINGLE_ROW;
  const { isMobile } = useMediaQuery() as MediaQueryContextType;

  const switchCalouselView = () => {
    setIsCarouselView(prev => !prev);
  };

  useEffect(() => {
    setFilteredMeetingsCount({
      filteredMeetingsLength: sessions,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions]);

  return (
    <div>
      {
        isCarouselViewByDay
          ? (
            <div role='columnheader' className={`${classNameSessionTableHeaderLeft} pb-8 px-5 flex items-center flex-wrap`}>
              <div className='flex'>
                <span
                  className={`${classNameEventDetailsDate} mb-2 text-primary font-size-14px ${Styles.headerCssClass} uppercase flex justify-start md:justify-end mr-3`}
                >
                  {selectedDate}
                </span>
                {isSessionsExist
                  ? (
                    <span
                      className={classNameSessionHeaderRight + 'flex mr-auto whitespace-no-wrap session-header'}
                    >
                      {`${sessions} ${sessions === count ? 'session' : 'sessions'}`}
                    </span>
                  )
                  : null
                }
              </div>
              { shouldRenderExpandButton &&
                <div className='flex-grow ml-2'>
                  <Button.Common
                    name='show-all'
                    className='button-expand flex mx-auto sm:mr-0 sm:ml-auto'
                    onClick={switchCalouselView}
                  >
                    {expandButtonText} <span className='button-expand-text'>{ shortDate }</span>
                  </Button.Common>
                </div>
              }
            </div>
          )
          : null
      }
      <div className='px-8'>
        {
          featuredMeetings.map((meeting: PrivateMeetingSessionDetailsResponseType) => (
            <CardComponent
              event={event}
              meeting={meeting}
              key={meeting.meetingId}
              eventToken={eventToken}
              transformedTimeZone={transformedTimeZone}
              onSubscriptionChange={onSubscriptionChange}
              isMobile={isMobile}
            />
          ))
        }
      </div>
      {
        !isCarouselViewByDay && renderSeparateLine(Boolean(featuredMeetings.length))
      }

      {isCarouselView ?
        <CarouselBox
          meetings={noFeaturedMeetings}
          event={event}
          groupByDay={isCarouselViewByDay}
          eventToken={eventToken}
          transformedTimeZone={transformedTimeZone}
          shouldRenderSeparateLine={false}
          onSubscriptionChange={onSubscriptionChange}
        />
        :
        <CarouselByGrid
          event={event}
          meetings={noFeaturedMeetings}
          transformedTimeZone={transformedTimeZone}
          eventToken={eventToken}
          onSubscriptionChange={onSubscriptionChange}
        />
      }
      { shouldRenderExpandButton &&
        <div className='flex w-full justify-center mt-4 m-bottom-30px'>
          <Button.Common
            name='show-all'
            className='button-expand flex'
            onClick={switchCalouselView}
          >
            {expandButtonText} <span className='button-expand-text'>{ shortDate }</span>
          </Button.Common>
        </div>
      }
      { renderSeparateLine(Boolean(meetings.length)) }
    </div>
  );
};

const CarouselWrapperByTrack = (props: ICarouselWrapper): JSX.Element => {
  const {
    event,
    meetings,
    selectedDate,
    showPastMeetings,
    searchedValue,
    setFilteredMeetingsCount,
    eventToken,
    transformedTimeZone,
    onSubscriptionChange,
  } = props;

  const {
    featuredMeetings,
    trackMeetings,
    noGroupMeetings
  } = useCarousel({ meetings, selectedDate, showPastMeetings, searchedValue });
  const { isMobile } = useMediaQuery() as MediaQueryContextType;

  useEffect(() => {
    setFilteredMeetingsCount({
      filteredMeetingsLength: featuredMeetings.length + trackMeetings.length + noGroupMeetings.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuredMeetings.length, trackMeetings.length, noGroupMeetings.length]);

  return (
    <div>
      {
        featuredMeetings.map((meeting: PrivateMeetingSessionDetailsResponseType) => (
          <CardComponent
            event={event}
            meeting={meeting}
            key={meeting.meetingId}
            eventToken={eventToken}
            transformedTimeZone={transformedTimeZone}
            isMobile={isMobile}
            onSubscriptionChange={onSubscriptionChange}
          />
        ))
      }
      <div className='p-top-1px'>
        {renderSeparateLine(Boolean(featuredMeetings.length))}
      </div>
      <CarouselBox
        meetings={trackMeetings}
        event={event}
        eventToken={eventToken}
        transformedTimeZone={transformedTimeZone}
        shouldRenderSeparateLine={false}
        onSubscriptionChange={onSubscriptionChange}
      />
      <CarouselBox
        meetings={noGroupMeetings}
        event={event}
        eventToken={eventToken}
        transformedTimeZone={transformedTimeZone}
        shouldRenderSeparateLine={false}
        onSubscriptionChange={onSubscriptionChange}
      />
    </div>
  );
};

export { CarouselWrapperByDay, CarouselWrapperByTrack };